export interface ZohoDeskUser {
  id: string;
  emailId: string;
  name: string;
  profileName?: string;
}

export interface ZohoDeskOrganization {
  id: string;
  domain?: string; // 'com' | 'eu' | 'in' | 'com.au' | 'jp' | 'com.cn' | 'sa'
}

export interface ZohoDeskAppInstance {
  user: ZohoDeskUser;
  organization: ZohoDeskOrganization;
}

export interface ZohoDeskApp {
  instance: ZohoDeskAppInstance;
  // emit/on/getWidgets/etc. omitted for F0 — added in F1+
}

interface ZohoDeskGlobal {
  extension: {
    onload: () => Promise<ZohoDeskApp>;
  };
}

export interface LoadZohoSdkOptions {
  scriptUrl?: string;
}

const DEFAULT_SCRIPT =
  'https://js.zohostatic.com/support/developer_sdk/v1/js/ZohoDeskClientSDK.min.js';

export async function loadZohoDeskSdk(opts: LoadZohoSdkOptions = {}): Promise<ZohoDeskApp> {
  const existing = (globalThis as unknown as { ZOHODESK?: ZohoDeskGlobal }).ZOHODESK;
  if (existing?.extension?.onload) {
    return existing.extension.onload();
  }
  await new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = opts.scriptUrl ?? DEFAULT_SCRIPT;
    script.async = true;
    const timeout = setTimeout(() => {
      script.remove();
      reject(new Error('Zoho Desk SDK load timeout'));
    }, 10_000);
    script.onload = () => {
      clearTimeout(timeout);
      resolve();
    };
    script.onerror = () => {
      clearTimeout(timeout);
      script.remove();
      reject(new Error('failed to load Zoho Desk SDK'));
    };
    document.head.appendChild(script);
  });
  const sdk = (globalThis as unknown as { ZOHODESK?: ZohoDeskGlobal }).ZOHODESK;
  if (!sdk?.extension?.onload) {
    throw new Error('Zoho Desk SDK loaded but ZOHODESK.extension.onload is missing');
  }
  return sdk.extension.onload();
}
